/**
 * CampaignDashboard — FB Campaign Panel
 * Timeline view, Next week preview, Color-coded performance
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import { trackEvent } from '@/utils/tracking';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import {
  Send, TrendingUp, TrendingDown, MessageCircle, ThumbsUp,
  MousePointerClick, Eye, Star, Pin, Recycle, Trophy,
  ChevronDown, ChevronUp, Calendar, Clock, Zap, Sparkles,
  Edit3, X, Check, ArrowRight, BarChart3, Target, RefreshCw, ImageIcon
} from 'lucide-react';

/* ═══ CONSTANTS ═══ */
const TYPE_LABELS = {
  problem: "Problem", insight: "Spostrzeżenie", test: "Demo/test",
  value: "Wartość", micro_result: "Wynik", education: "Edukacja",
  cta: "Wezwanie", offer: "Oferta", social_proof: "Opinie",
  behind_scenes: "Kulisy", engagement: "Angażujący", news: "Nowość",
  observation: "Obserwacja", myth_busting: "Obalenie mitu",
  behind_the_system: "Kulisy kampanii", mini_case: "Mini case",
  why_most_marketing_fails: "Dlaczego marketing nie działa",
  proof: "Dowód",
};

const INTENT_LABELS = {
  education: "Edukacja", proof: "Dowód", engagement: "Zaangażowanie", lead: "Lead",
};

const INTENT_COLORS = {
  education: "bg-blue-50 text-blue-700 border-blue-200",
  proof: "bg-emerald-50 text-emerald-700 border-emerald-200",
  engagement: "bg-purple-50 text-purple-700 border-purple-200",
  lead: "bg-orange-50 text-orange-700 border-orange-200",
};

const LABEL_CONFIG = {
  pinned_candidate: { icon: Pin, label: "Przypięty", color: "text-blue-600 bg-blue-50 border-blue-200" },
  best_performer_candidate: { icon: Trophy, label: "Najlepszy", color: "text-amber-600 bg-amber-50 border-amber-200" },
  recycle_candidate: { icon: Recycle, label: "Do przerobienia", color: "text-zinc-500 bg-zinc-50 border-zinc-200" },
};

function perfColor(score) {
  if (score >= 20) return { bg: "bg-emerald-500", text: "text-emerald-600", ring: "ring-emerald-200", label: "Działa", bar: "bg-emerald-400", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (score >= 10) return { bg: "bg-emerald-400", text: "text-emerald-600", ring: "ring-emerald-200", label: "Dobrze", bar: "bg-emerald-300", badge: "bg-emerald-50 text-emerald-600 border-emerald-200" };
  if (score >= 5) return { bg: "bg-amber-400", text: "text-amber-600", ring: "ring-amber-200", label: "Rośnie", bar: "bg-amber-300", badge: "bg-amber-50 text-amber-700 border-amber-200" };
  if (score > 0) return { bg: "bg-amber-300", text: "text-amber-600", ring: "ring-amber-200", label: "Start", bar: "bg-amber-200", badge: "bg-amber-50 text-amber-600 border-amber-200" };
  return { bg: "bg-zinc-300", text: "text-zinc-500", ring: "ring-zinc-200", label: "Zbiera dane", bar: "bg-zinc-200", badge: "bg-zinc-50 text-zinc-500 border-zinc-200" };
}

function engagementScore(post) {
  const r = post.reactions || 0;
  const c = post.comments || 0;
  const m = post.messages || 0;
  const cl = post.clicks || 0;
  return r + (c * 2) + (m * 5) + cl;
}

function statusBadge(status, post = {}) {
  if (status === "published") return { label: "Opublikowany", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (status === "scheduled" && (post.retry_count || 0) > 0) return { label: `Blad (proba ${post.retry_count})`, cls: "bg-red-50 text-red-700 border-red-200" };
  if (status === "scheduled") return { label: "Gotowa", cls: "bg-[#F47A3B]/10 text-[#F47A3B] border-[#F47A3B]/20" };
  if (status === "draft") return { label: "Szkic", cls: "bg-zinc-50 text-zinc-500 border-zinc-200" };
  return { label: status || "Szkic", cls: "bg-zinc-50 text-zinc-500 border-zinc-200" };
}

/* ═══ SUMMARY CARD ═══ */
function CampaignSummary({ campaign, posts, weeklyReport }) {
  const published = posts.filter(p => p.status === "published");
  const totalReactions = published.reduce((s, p) => s + (p.reactions || 0), 0);
  const totalMessages = published.reduce((s, p) => s + (p.messages || 0), 0);
  const avgScore = published.length > 0
    ? Math.round(published.reduce((s, p) => s + (p.performance_score || 0), 0) / published.length)
    : 0;

  // Hide entirely when no meaningful stats
  const hasStats = totalReactions > 0 || totalMessages > 0 || avgScore > 0;
  if (!hasStats) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="mb-5"
      data-testid="campaign-summary"
    >
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-5 text-center" data-testid="summary-stat-reactions">
          <p className="text-3xl font-black text-emerald-600">{totalReactions}</p>
          <p className="text-[12px] text-zinc-500 mt-1 font-medium">Reakcji</p>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-orange-50 to-white border border-[#F47A3B]/20 p-5 text-center" data-testid="summary-stat-messages">
          <p className="text-3xl font-black text-[#F47A3B]">{totalMessages}</p>
          <p className="text-[12px] text-zinc-500 mt-1 font-medium">Wiadomosci</p>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-zinc-50 to-white border border-zinc-200 p-5 text-center" data-testid="summary-stat-score">
          <p className="text-3xl font-black text-zinc-800">{avgScore}<span className="text-base font-normal text-zinc-400"> pkt</span></p>
          <p className="text-[12px] text-zinc-500 mt-1 font-medium">Sr. wynik</p>
        </div>
      </div>
    </motion.div>
  );
}


/* ═══ NEXT WEEK PREVIEW ═══ */
function NextWeekPreview({ posts }) {
  const today = new Date().toISOString().slice(0, 10);
  const nextWeek = posts
    .filter(p => p.status === "scheduled" && p.scheduled_date > today)
    .slice(0, 7);

  if (nextWeek.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="rounded-2xl border border-zinc-200 bg-white p-5 mb-5"
      data-testid="next-week-preview"
    >
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-[#F47A3B]" />
        <h3 className="text-[13px] font-bold text-zinc-900">Kolejne posty w planie</h3>
        <span className="text-[10px] text-zinc-400 ml-auto">{nextWeek.length} gotowych</span>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {nextWeek.map((p, i) => {
          const perf = perfColor(p.performance_score || 0);
          return (
            <div key={p.id || i} className="rounded-lg bg-zinc-50 p-2 text-center border border-zinc-100 hover:border-zinc-300 transition-colors" data-testid={`next-week-slot-${i}`}>
              <p className="text-[9px] text-zinc-400 font-medium mb-1">D{p.day_index}</p>
              <p className="text-[10px] text-zinc-700 font-semibold leading-tight mb-1">{TYPE_LABELS[p.content_type] || p.content_type}</p>
              {p.brand_intent && (
                <span className={`inline-block px-1.5 py-0.5 rounded text-[8px] font-semibold border ${INTENT_COLORS[p.brand_intent] || "bg-zinc-50 text-zinc-500"}`}>
                  {INTENT_LABELS[p.brand_intent] || p.brand_intent}
                </span>
              )}
              <p className="text-[9px] text-zinc-400 mt-1">{p.scheduled_date?.slice(5)}</p>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}


/* ═══ TIMELINE POST ITEM ═══ */
function TimelinePost({ post, isToday, onSelect, isSelected }) {
  const score = post.performance_score || 0;
  const engagement = engagementScore(post);
  const perf = perfColor(score);
  const status = statusBadge(post.status, post);
  const labelConf = post.user_label ? LABEL_CONFIG[post.user_label] : null;
  const rawImg = post.image_url;
  const imageUrl = rawImg && rawImg !== 'None' && rawImg !== 'null'
    ? (rawImg.startsWith('/uploads') ? `/api${rawImg}` : rawImg)
    : null;
  const isPublished = post.status === 'published';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      onClick={() => onSelect(post)}
      className={`
        relative rounded-xl border cursor-pointer transition-all duration-200 overflow-hidden
        ${isToday ? "border-[#F47A3B] bg-[#F47A3B]/3 ring-2 ring-[#F47A3B]/10" : "border-zinc-200 bg-white hover:border-zinc-300"}
        ${isSelected ? "ring-2 ring-[#F47A3B]/30 border-[#F47A3B]/40" : ""}
      `}
      data-testid={`timeline-post-${post.day_index}`}
    >
      {/* Image — FB 4:5 format */}
      <div className="relative">
        {imageUrl ? (
          <div className="w-full aspect-[4/5] overflow-hidden bg-zinc-900">
            <img src={imageUrl} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-full bg-gradient-to-br from-[#0F172A] to-[#1E293B] flex items-center justify-center relative overflow-hidden aspect-[4/5]">
            <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 30% 50%, #F47A3B 1px, transparent 1px)', backgroundSize: '12px 12px'}} />
            <span className="text-[#F47A3B] font-bold text-[10px] tracking-widest z-10">APISAI</span>
          </div>
        )}

        {/* Engagement score badge — overlay on image */}
        {isPublished && (
          <div className={`absolute top-1.5 right-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold border backdrop-blur-sm ${perf.badge}`}
            data-testid={`engagement-badge-${post.day_index}`}
          >
            {engagement > 0 ? `${engagement} · ${perf.label}` : perf.label}
          </div>
        )}
      </div>

      <div className="p-3">
        {/* Today badge */}
        {isToday && (
          <div className="absolute top-0 left-3 px-2 py-0.5 rounded-b-lg bg-[#F47A3B] text-white text-[8px] font-bold tracking-wider z-10">
            DZISIAJ
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-zinc-400">D{post.day_index}</span>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${status.cls}`}>{status.label}</span>
          </div>
        </div>

        {/* Type + Intent */}
        <p className="text-[11px] font-semibold text-zinc-800 mb-1">{TYPE_LABELS[post.content_type] || post.content_type}</p>
        <div className="flex items-center gap-1 flex-wrap">
          {post.brand_intent && (
            <span className={`px-1.5 py-0.5 rounded text-[8px] font-semibold border ${INTENT_COLORS[post.brand_intent] || ""}`}>
              {INTENT_LABELS[post.brand_intent] || post.brand_intent}
            </span>
          )}
          {labelConf && (
            <span className={`px-1.5 py-0.5 rounded text-[8px] font-semibold border flex items-center gap-0.5 ${labelConf.color}`}>
              <labelConf.icon className="w-2.5 h-2.5" /> {labelConf.label}
            </span>
          )}
        </div>

        {/* Text preview */}
        {post.text && (
          <p className="text-[10px] text-zinc-500 mt-1.5 line-clamp-2 leading-relaxed">{post.text}</p>
        )}

        {/* Metrics bar (published only) */}
        {isPublished && (
          <div className="mt-2">
            <div className="flex items-center gap-3 text-[9px] text-zinc-400 mb-1.5">
              <span className="flex items-center gap-0.5"><ThumbsUp className="w-2.5 h-2.5" />{post.reactions || 0}</span>
              <span className="flex items-center gap-0.5"><MessageCircle className="w-2.5 h-2.5" />{post.comments || 0}</span>
              <span className="flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{post.messages || 0}</span>
              <span className={`ml-auto font-bold ${perf.text}`}>{score}pkt</span>
            </div>
            {/* Engagement bar */}
            <div className="w-full h-1 bg-zinc-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${perf.bar}`} style={{ width: `${Math.min(score * 3, 100)}%` }} />
            </div>
          </div>
        )}

        {/* Date */}
        <p className="text-[9px] text-zinc-300 mt-1.5">{post.scheduled_date}</p>

        {/* Publish button for scheduled posts */}
        {post.status === 'scheduled' && (
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(post); }}
            className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-[#F47A3B] text-white text-[10px] font-bold hover:bg-[#E06A2B] transition-colors"
            data-testid={`publish-btn-${post.day_index}`}
          >
            <Send className="w-3 h-3" /> Publikuj
          </button>
        )}
      </div>
    </motion.div>
  );
}


/* ═══ POST DETAIL PANEL ═══ */
function PostDetail({ post, onClose, onMark, onEdit, onRefreshImage }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post?.text || "");
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const perf = perfColor(post?.performance_score || 0);
  const status = statusBadge(post?.status, post);

  useEffect(() => { setEditText(post?.text || ""); setEditing(false); }, [post]);

  if (!post) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onEdit(post, editText);
      setEditing(false);
    } finally { setSaving(false); }
  };

  const handleRegenImage = async () => {
    setRegenerating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/fb-campaign/regenerate-image/${post.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.image_url && onRefreshImage) {
        onRefreshImage(post.id, res.data.image_url);
      }
    } catch (e) {
      console.error("Regenerate failed:", e);
    } finally { setRegenerating(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
      className="rounded-2xl border border-zinc-200 bg-white p-5 sticky top-4"
      data-testid="post-detail-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[13px] font-bold text-zinc-900">Dzień {post.day_index}</h3>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-zinc-100 text-zinc-400">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Status + Type */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${status.cls}`}>{status.label}</span>
        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-600">{TYPE_LABELS[post.content_type] || post.content_type}</span>
        {post.brand_intent && (
          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${INTENT_COLORS[post.brand_intent] || ""}`}>
            {INTENT_LABELS[post.brand_intent] || post.brand_intent}
          </span>
        )}
      </div>

      {/* Error details (if post has errors) */}
      {post.last_error && (
        <div className="mb-3 p-2.5 rounded-lg bg-red-50 border border-red-200" data-testid="post-error-detail">
          <p className="text-[11px] font-semibold text-red-700 mb-1">Blad publikacji</p>
          <p className="text-[10px] text-red-600 break-words">{post.last_error}</p>
          {post.retry_count > 0 && (
            <p className="text-[10px] text-red-500 mt-1">Prob: {post.retry_count} | Ostatnia: {post.last_attempt_at ? new Date(post.last_attempt_at).toLocaleString('pl-PL') : '—'}</p>
          )}
        </div>
      )}

      {/* Image preview (FB-style) */}
      {(() => {
        const rawImg = post.image_url;
        const imgUrl = rawImg && rawImg !== 'None' && rawImg !== 'null'
          ? (rawImg.startsWith('/uploads') ? `/api${rawImg}` : rawImg)
          : null;
        return (
          <div className="mb-4">
            {imgUrl ? (
              <div className="rounded-xl overflow-hidden border border-zinc-100">
                <img src={imgUrl} alt="Post" className="w-full h-auto object-cover" />
              </div>
            ) : (
              <div className="rounded-xl overflow-hidden border border-zinc-100">
                <div className="w-full h-24 bg-gradient-to-br from-[#0F172A] to-[#1E293B] flex items-center justify-center relative">
                  <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 30% 50%, #F47A3B 1px, transparent 1px)', backgroundSize: '16px 16px'}} />
                  <div className="z-10 text-center">
                    <span className="text-[#F47A3B] font-bold text-xs tracking-widest">APISAI</span>
                    <p className="text-zinc-500 text-[9px] mt-0.5">Grafika wygenerowana przy publikacji</p>
                  </div>
                </div>
              </div>
            )}
            <button
              onClick={handleRegenImage}
              disabled={regenerating}
              className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 text-zinc-600 text-[11px] font-semibold transition-all disabled:opacity-50"
              data-testid="regen-image-btn"
            >
              {regenerating ? (
                <><div className="w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" /> Generowanie...</>
              ) : (
                <><Sparkles className="w-3 h-3" /> Generuj nowa grafike</>
              )}
            </button>
          </div>
        );
      })()}

      {/* Engagement Score — prominent */}
      {post.status === 'published' && (() => {
        const eng = engagementScore(post);
        const score = post.performance_score || 0;
        const p = perfColor(score);
        return (
          <div className={`mb-4 rounded-xl border ${p.badge} p-3`} data-testid="post-engagement-score">
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-10 h-10 rounded-xl ${p.bg} flex items-center justify-center`}>
                <span className="text-white text-[14px] font-black">{eng}</span>
              </div>
              <div>
                <p className={`text-[13px] font-bold ${p.text}`}>{p.label}</p>
                <p className="text-[10px] text-zinc-400">{score} pkt systemowych · {eng} engagement</p>
              </div>
            </div>
            <div className="w-full h-2 bg-white/60 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-700 ${p.bar}`} style={{ width: `${Math.min(score * 3, 100)}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[9px] text-zinc-400">
              <span>Reakcje {post.reactions || 0} · Komentarze {post.comments || 0} · Wiadomości {post.messages || 0}</span>
              <span className={`font-semibold ${p.text}`}>{score >= 20 ? 'TOP' : score >= 10 ? 'Dobry' : score >= 5 ? 'W rozwoju' : 'Nowy'}</span>
            </div>
          </div>
        );
      })()}

      {/* Performance (non-published) */}
      {post.status !== 'published' && (
        <div className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg ${perf.bg}/10 ring-1 ${perf.ring}`}>
          <div className={`w-3 h-3 rounded-full ${perf.bg}`} />
          <span className={`text-[12px] font-bold ${perf.text}`}>{post.performance_score || 0} pkt</span>
          <span className={`text-[10px] ${perf.text}`}>{perf.label}</span>
          {post.quality_score != null && (
            <span className="text-[10px] text-zinc-400 ml-auto">QS: {post.quality_score}</span>
          )}
        </div>
      )}

      {/* Text */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[10px] text-zinc-400 font-semibold">TREŚĆ POSTA</p>
          {!editing && post.status !== "published" && (
            <button onClick={() => setEditing(true)} className="text-[10px] text-[#F47A3B] font-semibold flex items-center gap-0.5 hover:underline">
              <Edit3 className="w-3 h-3" /> Edytuj
            </button>
          )}
        </div>
        {editing ? (
          <div>
            <textarea
              value={editText} onChange={e => setEditText(e.target.value)}
              className="w-full rounded-lg border border-zinc-300 p-2.5 text-[12px] text-zinc-700 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30"
              rows={6} data-testid="post-edit-textarea"
            />
            <div className="flex gap-1.5 mt-2">
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#F47A3B] hover:bg-[#E07F0A] text-white text-[11px]" data-testid="post-edit-save">
                {saving ? "..." : <><Check className="w-3 h-3 mr-1" /> Zapisz</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditText(post.text); }} className="text-[11px]">
                Anuluj
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-[12px] text-zinc-700 leading-relaxed whitespace-pre-wrap bg-zinc-50 rounded-lg p-3" data-testid="post-detail-text">
            {post.text || "Brak treści"}
          </p>
        )}
      </div>

      {/* Metrics (published) */}
      {post.status === "published" && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            { icon: ThumbsUp, label: "Reakcje", value: post.reactions || 0 },
            { icon: MessageCircle, label: "Komentarze", value: post.comments || 0 },
            { icon: Eye, label: "Wiadomości", value: post.messages || 0 },
            { icon: MousePointerClick, label: "Kliknięcia", value: post.clicks || 0 },
          ].map(m => (
            <div key={m.label} className="text-center p-2 rounded-lg bg-zinc-50" data-testid={`detail-metric-${m.label.toLowerCase()}`}>
              <m.icon className="w-3 h-3 text-zinc-400 mx-auto mb-0.5" />
              <p className="text-sm font-bold text-zinc-800">{m.value}</p>
              <p className="text-[8px] text-zinc-400">{m.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Mark buttons */}
      <div className="mb-3">
        <p className="text-[10px] text-zinc-400 font-semibold mb-2">OZNACZ POST</p>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(LABEL_CONFIG).map(([key, conf]) => {
            const isActive = post.user_label === key;
            return (
              <button
                key={key}
                onClick={() => onMark(post, isActive ? "none" : key)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold border transition-all
                  ${isActive ? conf.color + " ring-2 ring-offset-1 " + conf.color.split(' ')[0].replace('text','ring') : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-300"}`}
                data-testid={`mark-${key}`}
              >
                <conf.icon className="w-3 h-3" /> {conf.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Meta */}
      <div className="text-[9px] text-zinc-300 space-y-0.5">
        <p>Data: {post.scheduled_date} · {post.publish_time || "18:00"}</p>
        {post.source_url && <p>Link: {post.source_url}</p>}
        {post.content_source && <p>Zrodlo: {post.content_source}</p>}
      </div>

      {/* Publish button */}
      {post.status === 'scheduled' && (
        <PublishButton post={post} />
      )}
    </motion.div>
  );
}


/* ═══ PUBLISH BUTTON ═══ */
function PublishButton({ post }) {
  const [publishing, setPublishing] = useState(false);

  const handlePublish = async () => {
    if (!window.confirm('Opublikowac ten post na Facebooku teraz?')) return;
    setPublishing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/fb-campaign/${post.campaign_id}/posts/${post.id}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.ok) {
        toast.success('Post opublikowany na Facebooku!');
        window.location.reload();
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Blad publikacji';
      toast.error(msg);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <button
      onClick={handlePublish}
      disabled={publishing}
      className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#F47A3B] text-white text-[13px] font-bold hover:bg-[#E06A2B] transition-all disabled:opacity-50 shadow-lg shadow-[#F47A3B]/20"
      data-testid={`publish-post-btn-${post.day_index}`}
    >
      {publishing ? (
        <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Publikowanie...</>
      ) : (
        <><Send className="w-4 h-4" /> Publikuj na Facebook</>
      )}
    </button>
  );
}


/* ═══ FILTERS ═══ */
function Filters({ filters, onChange }) {
  return (
    <div className="flex gap-2 mb-5" data-testid="campaign-filters">
      {["all", "published", "scheduled"].map(s => (
        <button
          key={s}
          onClick={() => onChange({ status: s, type: null, intent: null })}
          className={`px-4 py-2 rounded-xl text-[12px] font-bold transition-colors
            ${filters.status === s ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}
          data-testid={`filter-status-${s}`}
        >
          {s === "all" ? "Wszystkie" : s === "published" ? "Opublikowane" : "Gotowe"}
        </button>
      ))}
    </div>
  );
}


/* ═══ REFRESH GRAPHICS BANNER ═══ */
function RefreshGraphicsBanner({ onRefresh, eligibleCount, refreshing }) {
  if (!eligibleCount || eligibleCount <= 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4 bg-gradient-to-r from-[#FFF7ED] to-[#FFFBF5] border border-[#F47A3B]/20 rounded-2xl p-4 flex items-center gap-4"
      data-testid="refresh-graphics-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
        <ImageIcon className="w-5 h-5 text-[#F47A3B]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-bold text-zinc-800">
          Chcesz odświeżyć grafiki w zaplanowanych postach?
        </p>
        <p className="text-[11px] text-zinc-500 mt-0.5">
          {eligibleCount} {eligibleCount === 1 ? 'grafika' : eligibleCount < 5 ? 'grafiki' : 'grafik'} z aktualnymi danymi Twojego konta
        </p>
      </div>
      <button
        onClick={onRefresh}
        disabled={refreshing}
        className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#F47A3B] text-white text-[12px] font-bold hover:bg-[#E06A2B] transition-colors disabled:opacity-50 flex-shrink-0"
        data-testid="refresh-graphics-btn"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        {refreshing ? 'Odświeżam...' : 'Odśwież'}
      </button>
    </motion.div>
  );
}


/* ═══ MAIN COMPONENT ═══ */
export default function CampaignDashboard() {
  const { t } = useTranslation();
  const [campaign, setCampaign] = useState(null);
  const [posts, setPosts] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [filters, setFilters] = useState({ status: "all", type: null, intent: null });
  const [refreshEligible, setRefreshEligible] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshDismissed, setRefreshDismissed] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    const hdrs = { Authorization: `Bearer ${token}` };
    try {
      const [campRes, reportRes, refreshRes] = await Promise.all([
        axios.get(`${API}/fb-campaign/active`, { headers: hdrs }),
        axios.get(`${API}/report/weekly`, { headers: hdrs }).catch(() => ({ data: null })),
        axios.get(`${API}/fb-campaign/refresh-check`, { headers: hdrs }).catch(() => ({ data: { should_refresh: false } })),
      ]);
      if (campRes.data?.campaign) {
        setCampaign(campRes.data.campaign);
        setPosts(campRes.data.posts || []);
      }
      if (reportRes.data) setWeeklyReport(reportRes.data);
      if (refreshRes.data?.should_refresh) {
        setRefreshEligible(refreshRes.data.eligible_count || 0);
      }
    } catch (e) {
      console.error("Campaign fetch error:", e);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Track page view
  useEffect(() => { trackEvent('view_campaign_dashboard'); }, []);

  const handleMark = async (post, label) => {
    try {
      await axios.put(`${API}/fb-campaign/${campaign.id}/posts/${post.id}/mark`, { label }, { headers });
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, user_label: label === "none" ? null : label } : p));
      if (selectedPost?.id === post.id) setSelectedPost(sp => ({ ...sp, user_label: label === "none" ? null : label }));
    } catch (e) { console.error("Mark error:", e); }
  };

  const handleEdit = async (post, newText) => {
    try {
      await axios.put(`${API}/fb-campaign/${campaign.id}/posts/${post.id}`, { text: newText }, { headers });
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, text: newText, edited_by_user: true, locked_by_user: true, content_source: "user_edit" } : p));
      if (selectedPost?.id === post.id) setSelectedPost(sp => ({ ...sp, text: newText }));
    } catch (e) { console.error("Edit error:", e); }
  };

  const handleRefreshGraphics = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/fb-campaign/refresh-scheduled-graphics`, {}, { headers });
      const count = res.data?.refreshed || 0;
      toast.success(`Odświeżono ${count} grafik z aktualnymi danymi`);
      setRefreshDismissed(true);
      setRefreshEligible(0);
      // Refetch data after a short delay to pick up updated images
      setTimeout(() => fetchData(), 3000);
    } catch (e) {
      toast.error('Nie udało się odświeżyć grafik');
    } finally {
      setRefreshing(false);
    }
  };

  const [syncing, setSyncing] = useState(false);

  const handleSyncFromFB = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/fb-campaign/sync-published`, {}, { headers });
      if (res.data?.ok) {
        toast.success(t('campaign.syncSuccess', { count: res.data.synced }));
        fetchData(); // Refresh data
      } else {
        toast.error(res.data?.message || 'Nie udało się zsynchronizować');
      }
    } catch (e) {
      toast.error('Błąd synchronizacji z FB');
    } finally {
      setSyncing(false);
    }
  };

  // Filter posts
  const filtered = posts.filter(p => {
    if (filters.status !== "all" && p.status !== filters.status) return false;
    if (filters.type && p.content_type !== filters.type) return false;
    if (filters.intent && p.brand_intent !== filters.intent) return false;
    return true;
  });

  // Split for timeline
  const today = new Date().toISOString().slice(0, 10);
  const published = filtered.filter(p => p.status === "published").sort((a, b) => b.day_index - a.day_index);
  const upcoming = filtered.filter(p => p.status !== "published").sort((a, b) => a.day_index - b.day_index);
  const failed = posts.filter(p => (p.retry_count || 0) > 0 && p.status !== "published");
  const allPublished = posts.filter(p => p.status === "published");
  const allScheduled = posts.filter(p => p.status === "scheduled");

  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-6 h-6 border-2 border-[#F47A3B] border-t-transparent rounded-full" />
        </div>
      </PanelLayout>
    );
  }

  if (!campaign) {
    return (
      <PanelLayout>
        <div className="max-w-lg mx-auto text-center py-12" data-testid="no-campaign">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
              <Target className="w-3 h-3 text-[#F47A3B]" />
              <span className="text-[11px] font-semibold text-[#F47A3B]">Marketing</span>
            </div>
            <h2 className="text-2xl font-extrabold text-zinc-900 mb-2">Kampania FB</h2>
            <p className="text-[15px] text-zinc-500 mb-8 max-w-md mx-auto">
              System automatycznie tworzy i publikuje posty na Twoim Facebooku — Ty nie musisz nic robić.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl border border-zinc-200 p-6 mb-6 text-left">
            <h3 className="text-sm font-bold text-zinc-900 mb-4">Jak to działa?</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-4 h-4 text-[#F47A3B]" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-zinc-800">System pisze posty za Ciebie</p>
                  <p className="text-[12px] text-zinc-500">Na podstawie Twojego profilu — dopasowane do Twojej oferty</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Calendar className="w-4 h-4 text-emerald-500" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-zinc-800">Publikuje automatycznie</p>
                  <p className="text-[12px] text-zinc-500">Harmonogram dopasowany do najlepszych godzin</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <BarChart3 className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-zinc-800">Zbiera wyniki i się uczy</p>
                  <p className="text-[12px] text-zinc-500">Kolejne posty są coraz lepsze — system optymalizuje treści</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="space-y-3">
            <p className="text-[12px] text-zinc-400 mb-2">Zacznij od uzupełnienia profilu — na jego podstawie system tworzy kampanię</p>
            <div className="flex items-center justify-center gap-3">
              <Button
                onClick={() => window.location.href = '/panel/profil'}
                className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl px-8 py-3 text-[14px] font-bold shadow-lg shadow-[#F47A3B]/20"
                data-testid="no-campaign-cta-profile"
              >
                Uzupełnij profil <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => window.location.href = '/panel/publikacja'}
                variant="outline"
                className="rounded-xl px-6 py-3 text-[14px] font-semibold border-zinc-200"
                data-testid="no-campaign-cta-publish"
              >
                Opublikuj post
              </Button>
            </div>
          </motion.div>
        </div>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout>
      <div className="max-w-6xl mx-auto" data-testid="campaign-dashboard">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
            <Target className="w-3.5 h-3.5 text-[#F47A3B]" />
            <span className="text-[12px] font-semibold text-[#F47A3B]">Twoje reklamy</span>
          </div>
          <h1 className="text-3xl font-extrabold text-zinc-900 mb-2" data-testid="campaign-page-title">
            Masz {posts.length} {posts.length === 1 ? 'gotowa reklame' : posts.length < 5 ? 'gotowe reklamy' : 'gotowych reklam'}
          </h1>
          <p className="text-[14px] text-zinc-500 max-w-md mx-auto mb-3">
            {allPublished.length > 0
              ? `${allPublished.length} opublikowanych, ${allScheduled.length} czeka na publikacje`
              : 'Kazda reklama jest gotowa do publikacji na Facebooku'
            }
          </p>
          {/* Status Counters */}
          <div className="flex items-center justify-center gap-3 mb-2" data-testid="campaign-status-counters">
            {allPublished.length > 0 && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200">
                <Check className="w-3 h-3 text-emerald-600" />
                <span className="text-[12px] font-bold text-emerald-700" data-testid="published-count">{allPublished.length}</span>
                <span className="text-[11px] text-emerald-600">opublikowanych</span>
              </div>
            )}
            {allScheduled.length > 0 && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/20">
                <Sparkles className="w-3 h-3 text-[#F47A3B]" />
                <span className="text-[12px] font-bold text-[#F47A3B]" data-testid="scheduled-count">{allScheduled.length}</span>
                <span className="text-[11px] text-[#F47A3B]">gotowych do publikacji</span>
              </div>
            )}
            {failed.length > 0 && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 border border-red-200">
                <X className="w-3 h-3 text-red-600" />
                <span className="text-[12px] font-bold text-red-700" data-testid="failed-count">{failed.length}</span>
                <span className="text-[11px] text-red-600">z bledem</span>
              </div>
            )}
          </div>
          {/* Sync from FB button */}
          {allPublished.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncFromFB}
              disabled={syncing}
              className="mt-3 gap-2 text-[12px] rounded-xl border-zinc-200 hover:border-[#F47A3B]/30 hover:bg-[#FFF3EB]"
              data-testid="sync-from-fb-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Synchronizacja...' : 'Synchronizuj z FB'}
            </Button>
          )}
        </div>

        {/* Summary */}
        <CampaignSummary campaign={campaign} posts={posts} weeklyReport={weeklyReport} />

        {/* Refresh Graphics Banner */}
        {!refreshDismissed && refreshEligible > 0 && (
          <RefreshGraphicsBanner
            eligibleCount={refreshEligible}
            onRefresh={handleRefreshGraphics}
            refreshing={refreshing}
          />
        )}

        {/* Filters */}
        <Filters filters={filters} onChange={setFilters} />

        {/* Timeline + Detail split */}
        <div className="flex gap-5">
          {/* Timeline */}
          <div className="flex-1 min-w-0">
            {/* Published section */}
            {published.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <h3 className="text-[12px] font-bold text-zinc-500 uppercase tracking-wider">Opublikowane ({published.length})</h3>
                </div>
                <div className="grid grid-cols-3 xl:grid-cols-4 gap-3">
                  {published.map(p => (
                    <TimelinePost
                      key={p.id}
                      post={p}
                      isToday={p.scheduled_date === today}
                      onSelect={setSelectedPost}
                      isSelected={selectedPost?.id === p.id}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Divider */}
            {published.length > 0 && upcoming.length > 0 && (
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-zinc-200" />
                <span className="text-[10px] text-zinc-400 font-semibold flex items-center gap-1"><Clock className="w-3 h-3" /> Gotowe do publikacji</span>
                <div className="flex-1 h-px bg-zinc-200" />
              </div>
            )}

            {/* Upcoming section */}
            {upcoming.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-[#F47A3B]" />
                  <h3 className="text-[12px] font-bold text-zinc-500 uppercase tracking-wider">Gotowe reklamy ({upcoming.length})</h3>
                </div>
                <div className="grid grid-cols-3 xl:grid-cols-4 gap-3">
                  {upcoming.map(p => (
                    <TimelinePost
                      key={p.id}
                      post={p}
                      isToday={p.scheduled_date === today}
                      onSelect={setSelectedPost}
                      isSelected={selectedPost?.id === p.id}
                    />
                  ))}
                </div>
              </div>
            )}

            {filtered.length === 0 && (
              <div className="text-center py-12 text-zinc-400">
                <p className="text-[13px]">Brak postów pasujących do filtrów</p>
              </div>
            )}
          </div>

          {/* Detail panel (sticky right) */}
          <AnimatePresence>
            {selectedPost && (
              <div className="w-80 flex-shrink-0">
                <PostDetail
                  post={selectedPost}
                  onClose={() => setSelectedPost(null)}
                  onMark={handleMark}
                  onEdit={handleEdit}
                  onRefreshImage={(postId, newUrl) => {
                    setPosts(prev => prev.map(p => p.id === postId ? { ...p, image_url: newUrl } : p));
                    setSelectedPost(prev => prev?.id === postId ? { ...prev, image_url: newUrl } : prev);
                  }}
                />
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </PanelLayout>
  );
}
